import {
    all,
    call,
    delay,
    put,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import storeNames from '@elab/components/connected/forms/CheckoutForms/storeNames';
import { selectors as freeSampleSelectors } from '@elab/components/connected/FreeSamples/redux';
import { getTypes } from '@elab/components/connected/forms/CheckoutForms/redux';
import { selectors as rtsSelectors } from '@elab/components/connected/real-time-stock/redux';
import { selectors as globalSelectors } from '@elab/core/store/global';
import constants from '@elab/core/utils/constants';

import { types as globalTypes } from '../global';
import analytics from '../../utils/analytics';
import { isExternalURL } from '../../utils/url';
import { selectors as cartSelectors, types as cartTypes } from '../cart';
import { selectors as paymentSelectors } from '../payment';
import { selectors as orderSelectors, types as orderTypes } from '../order';
import { selectors as reviewsSelectors } from '../reviews';
import { selectors as productSelectors, types as productTypes } from '../product';
import { types as authTypes } from '../auth';
import { selectors as userSelectors, types as userTypes } from '../user';
import { types as uiTypes } from '../ui';
import isEmpty from '../../utils/isEmpty';
import { selectors as configSelectors } from '../config';

import actions from './actions';
import selectors from './selectors';
import types from './types';

const checkoutAuthOptionsTypes = dynaForms.getTypes(storeNames.CHECKOUT_AUTH_OPTIONS);

const checkoutAuthOptionsSelectors = dynaForms.getSelectors(storeNames.CHECKOUT_AUTH_OPTIONS);
const PAYMENT_OPTION = 'payment';
const PRODUCT_WRAP_MODAL = 'product-wrap-modal';
const PRODUCT_RTS_MODAL = 'rts-pickup';
const PRODUCT_REVIEW_MODAL = 'review-popup';
const CHECKOUT_REGISTRATION = 'checkout-registration';

export function getImpressionViewsToolset(type) {
    const toolsetMap = {
        product: {
            selector: selectors.getCollectedProductImpressions,
            handler: analytics.handleProductImpressions,
        },
        promotion: {
            selector: selectors.getCollectedPromoImpressions,
            handler: analytics.handlePromoImpressions,
        },
        story: {
            selector: selectors.getCollectedStoryImpressions,
            handler: analytics.handleStoryImpressions,
        },
    };
    return toolsetMap[type] || {};
}

export function* sendImpressionViewsData({ payload: { label: labelFromView, trackerType } }) {
    yield delay(100);
    const { handler, selector } = yield call(getImpressionViewsToolset, trackerType);
    const impressions = yield select(selector) || [];
    const isLoyalty = yield select(userSelectors.isLoyalty);
    yield spawn(handler, {
        label: labelFromView || (yield select(selectors.getCurrentPage)),
        impressions: impressions.map(impression => ({
            ...impression,
            isVip: isLoyalty || false,
        })),
    });
    yield put(actions.clearImpressions(trackerType));
}

export function* sendImpressionClickData({
    payload: { trackerType, label: labelFromView, data: impression },
}) {
    yield delay(100);
    const isLoyalty = yield select(userSelectors.isLoyalty);
    const handler =
        trackerType === 'product'
            ? analytics.handleProductImpressionClick
            : analytics.handlePromoImpressionClick;
    yield spawn(handler, {
        label: labelFromView || (yield select(selectors.getCurrentPage)),
        impression: { ...impression, isVip: isLoyalty || false },
    });
}

export function* sendStoryImpressionViewsData({ payload: { trackerType } }) {
    yield delay(100);
    const { handler, selector } = yield call(getImpressionViewsToolset, trackerType);
    yield spawn(handler, {
        impressions: yield select(selector),
    });
    yield put(actions.clearImpressions(trackerType));
}

export function* sendStoryImpressionClickData({ payload: { data: impression } }) {
    yield delay(100);
    const handler = analytics.handleStoryImpressionClick;
    yield spawn(handler, {
        impression,
    });
}

export function* sendReviewsImpressionViewsData({ payload }) {
    const { rating } = payload;
    yield delay(100);
    const { productCode, productBrand, productName } = yield select(
        productSelectors.getProductCodeForAddProductReview,
    );
    const reviewCount = yield select(reviewsSelectors.getTotalCount) || 0;
    const avgReviewScore = rating ? +rating.replace(',', '.') : undefined;

    const analyticsData = {
        productCode,
        productName,
        productBrand,
        reviewCount,
        avgReviewScore,
    };

    yield spawn(analytics.handleReviewsImpressionView, analyticsData);
}

export function* sendViewEventWaiting({ payload }) {
    if (payload) {
        yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
        if (payload.type === 'checkout-step') {
            yield put(actions.setAdditionalStep(false));
        }
        const isLoyalty = yield select(userSelectors.isLoyalty);
        let cartEntries = yield select(cartSelectors.getEntries);
        if (!cartEntries) {
            yield take(cartTypes.SET_CART_DATA);
            cartEntries = yield select(cartSelectors.getEntries);
        }

        const { products } = payload;
        let entries;
        if (products) {
            if (Array.isArray(products)) {
                entries = cartEntries.filter(entry =>
                    products.some(item => item.id === entry.product.code),
                );
            } else {
                entries = cartEntries.filter(
                    entry => products.id || products.code === entry.product.code,
                );
            }
        }
        const thresholdConfig = yield select(globalSelectors.getThresholdConfig);
        if (thresholdConfig && thresholdConfig.isThresholdPromotionEnabled === 'true') {
            let thresholdPromotion = yield select(productSelectors.getThresholdPromotionObject);
            if (!thresholdPromotion) {
                yield take(productTypes.SET_THRESHOLD_PROMOTION_OBJECT);
                thresholdPromotion = yield select(productSelectors.getThresholdPromotionObject);
            }
            if (thresholdPromotion && thresholdPromotion.minimumPurchaseAmount) {
                payload.thresholdPromotion = true;
            }
        }

        // take product data from cart
        payload.products = entries;
        payload.entries = cartEntries;
        yield call(analytics.handleView, { ...payload, isLoyalty: isLoyalty || false });
    }
}

export function* sendTransactionWaiting({ payload }) {
    if (payload) {
        yield put(actions.proceedPageload(true));
        yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
        yield put(actions.proceedPageload(false));
        yield call(analytics.handleTransaction, payload);
    }
}

export function checkForOutboundLink() {
    const links = document.querySelectorAll('a:not([data-prevent-listener])');

    for (const link of links) {
        if (link.href && isExternalURL(link.href)) {
            link.addEventListener('click', () => {
                analytics.handleOutboundLinks(link.href);
            });
        }
    }
}

export function checkoutAnalyticsData(
    step,
    option,
    hasDoubleSecondStep = false,
    additionalStep = false,
) {
    let optionType;
    if (!hasDoubleSecondStep) {
        switch (step) {
            case 1:
                optionType = 'deliveryModes';
                break;
            case 2:
                optionType = 'checkoutOptions';
                break;
            default:
                optionType = 'paymentOptions';
        }
    } else {
        switch (step) {
            case 1:
                optionType = '';
                break;
            case 2:
                optionType = 'deliveryModes';
                break;
            default:
                optionType = 'paymentOptions';
        }
    }

    const currentStep = step + (additionalStep ? 1 : 0);

    return {
        label: `step ${currentStep}`,
        step: currentStep,
        option: { type: optionType, value: option },
    };
}

export function* sendCheckoutCartEvents() {
    const { type = '' } = yield select(cartSelectors.getDeliveryMode);
    const step = yield select(selectors.getCurrentStep);

    if (step === 1) {
        yield spawn(analytics.handleCheckoutOptions, checkoutAnalyticsData(step, type));
    }
}

function* sendCheckoutPaymentEvents() {
    const [checkoutOption, paymentOption, currentStep, additionalStep] = yield all([
        select(state => checkoutAuthOptionsSelectors.getDataFieldValue(state, 'checkoutOptions')),
        select(paymentSelectors.getSelectedMethod),
        select(selectors.getCurrentStep),
        select(selectors.getAdditionalStep),
    ]);

    const analyticsStep = currentStep + (additionalStep ? 1 : 0);

    if (currentStep === 2) {
        yield spawn(
            analytics.handleCheckoutOptions,
            checkoutAnalyticsData(analyticsStep, checkoutOption),
        );
    }

    if (!isEmpty(paymentOption)) {
        yield spawn(
            analytics.handleCheckoutOptions,
            checkoutAnalyticsData(analyticsStep, paymentOption.name),
        );
    }
}

function* sendOrderEvents() {
    const [successfulOrderPaymentMode, paymentOption, currentStep] = yield all([
        select(orderSelectors.getPaymentMode),
        select(paymentSelectors.getSelectedMethod),
        select(selectors.getCurrentStep),
    ]);

    if (isEmpty(paymentOption) && successfulOrderPaymentMode.code === 'giftcard') {
        yield spawn(
            analytics.handleCheckoutOptions,
            checkoutAnalyticsData(currentStep, successfulOrderPaymentMode.code),
        );
    }
}

export function* onSetStep() {
    const step = yield select(selectors.getCurrentStep);
    const additionalStep = yield select(selectors.getAdditionalStep);
    const currentStep = step + (additionalStep ? 1 : 0);
    const collectProductsDataInStepOneOnly = yield select(
        configSelectors.collectProductsDataInStepOneOnly,
    );
    let entries;
    let totalPrice;
    let cartTotalItems;
    let appliedPromotions;
    let appliedVouchersAmount;
    let deliveryMode;
    let loyaltyValue;
    let giftCardValue;
    let paymentMode;

    if (step !== 1) {
        entries = yield select(cartSelectors.getEntries);
        if (!entries.length) {
            yield take(cartTypes.SET_CART_DATA);
        }
    }
    if (collectProductsDataInStepOneOnly) {
        deliveryMode = yield select(cartSelectors.getDeliveryMode);
        totalPrice = yield select(cartSelectors.getTotalPrice);
        appliedPromotions = yield select(cartSelectors.getPromotions);
        loyaltyValue = yield select(cartSelectors.getRewardLoyaltyPoints);
        cartTotalItems = yield select(cartSelectors.getTotalItems);
        appliedVouchersAmount = yield select(cartSelectors.getAppliedVouchersAmount);
        giftCardValue = yield select(cartSelectors.getGiftCardTransactions);
        paymentMode = yield select(cartSelectors.getPaymentMode);
    }

    if (step === 2) {
        yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
        entries = yield select(cartSelectors.getEntries);
        yield spawn(analytics.handleCheckoutStep, {
            step: currentStep,
            label: `step ${currentStep}`,
            entries,
            ...(collectProductsDataInStepOneOnly && {
                cartTotalPrice: totalPrice && totalPrice.value,
                cartTotalItems,
                couponValue:
                    appliedPromotions.length > 0
                        ? appliedPromotions[0].triggerCouponCode
                        : undefined,
                appliedVouchersValue: appliedVouchersAmount && appliedVouchersAmount.value,
                loyaltyValue,
                collectProductsDataInStepOneOnly,
                shippingTier: deliveryMode && deliveryMode.name,
                giftCardValue:
                    giftCardValue.length > 0 &&
                    giftCardValue[0].appliedAmount &&
                    giftCardValue[0].appliedAmount.value,
                paymentType: paymentMode && paymentMode.name,
            }),
        });
    }

    if (step === 3) {
        entries = yield select(cartSelectors.getEntries);
        const { type = '' } = yield select(cartSelectors.getDeliveryMode);
        const hasDoubleSecondStep = true;
        yield spawn(
            analytics.handleCheckoutOptions,
            checkoutAnalyticsData(2, type, hasDoubleSecondStep, additionalStep),
        );
        yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
        yield spawn(analytics.handleCheckoutStep, {
            step: currentStep,
            label: `step ${currentStep}`,
            entries,
            ...(collectProductsDataInStepOneOnly && {
                cartTotalPrice: totalPrice && totalPrice.value,
                cartTotalItems,
                couponValue:
                    appliedPromotions.length > 0
                        ? appliedPromotions[0].triggerCouponCode
                        : undefined,
                appliedVouchersValue: appliedVouchersAmount && appliedVouchersAmount.value,
                loyaltyValue,
                collectProductsDataInStepOneOnly,
                shippingTier: deliveryMode && deliveryMode.name,
                giftCardValue:
                    giftCardValue.length > 0 &&
                    giftCardValue[0].appliedAmount &&
                    giftCardValue[0].appliedAmount.value,
                paymentType: paymentMode && paymentMode.name,
            }),
        });
    }

    if (step === 4) {
        yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
        entries = yield select(cartSelectors.getEntries);
        yield spawn(analytics.handleCheckoutStep, {
            step: currentStep,
            label: `step ${currentStep}`,
            entries,
            ...(collectProductsDataInStepOneOnly && {
                cartTotalPrice: totalPrice && totalPrice.value,
                cartTotalItems,
                couponValue:
                    appliedPromotions.length > 0
                        ? appliedPromotions[0].triggerCouponCode
                        : undefined,
                appliedVouchersValue: appliedVouchersAmount && appliedVouchersAmount.value,
                loyaltyValue,
                collectProductsDataInStepOneOnly,
                shippingTier: deliveryMode && deliveryMode.name,
                giftCardValue:
                    giftCardValue.length > 0 &&
                    giftCardValue[0].appliedAmount &&
                    giftCardValue[0].appliedAmount.value,
                paymentType: paymentMode && paymentMode.name,
            }),
        });
    }
}

export function* onSetCheckoutOption({ payload }) {
    const checkedCheckoutOption = payload;
    const step = yield select(selectors.getCurrentStep);
    const loggedInOption = checkedCheckoutOption || storeNames.CHECKOUT_AUTH_OPTIONS;
    const additionalStep = yield select(selectors.getAdditionalStep);

    if (checkedCheckoutOption === 'checkout-registration') {
        yield call(analytics.handleCreateAccount, 1);
    }

    if (step === 1) {
        if (loggedInOption && !additionalStep) {
            yield put(actions.setAdditionalStep(true));

            const isPageLoad = yield select(selectors.getPageLoad);
            if (!isPageLoad) {
                yield take(globalTypes.PAGE_LOAD_EVENT_PUSHED);
            }

            const entries = yield select(cartSelectors.getEntries);

            yield spawn(analytics.handleCheckoutStep, {
                entries,
                ...checkoutAnalyticsData(2, loggedInOption),
            });
        }
    }
}

export function* onCheckoutLogin() {
    const step = yield select(selectors.getCurrentStep);
    const checkoutOption = yield select(selectors.getCheckoutOption);
    const loginOption = yield select(selectors.getLoginOption);
    const additionalStep = yield select(selectors.getAdditionalStep);
    const currentStep = step + (additionalStep ? 1 : 0);
    if (currentStep === 2 && checkoutOption === 'checkout-login' && !loginOption) {
        yield put(actions.setLoginOption(true));
        yield spawn(analytics.handleCheckoutOptions, checkoutAnalyticsData(2, checkoutOption));
    }
}

export function* getArgsFromCartData(optionType) {
    const [entries, coupon, discount, totalAmount] = yield all([
        select(cartSelectors.getEntries),
        select(cartSelectors.getAppliedVouchers),
        select(cartSelectors.getTotalDiscounts),
        select(cartSelectors.getAmountToPayValue),
    ]);
    let totalItems;
    let deliveryMode;
    const collectGAInfo = yield select(configSelectors.useCollectGAInformation);
    if (collectGAInfo) {
        deliveryMode = yield select(cartSelectors.getDeliveryMode);
        totalItems = yield select(cartSelectors.getTotalItems);
    }

    const giftCards =
        optionType === PAYMENT_OPTION ? yield select(cartSelectors.getGiftCardTransactions) : [];

    const option =
        optionType === PAYMENT_OPTION
            ? totalAmount === 0 && giftCards.length
                ? { name: 'gift card' }
                : yield select(paymentSelectors.getSelectedMethod)
            : yield select(cartSelectors.getDeliveryMode);

    const loyalty =
        optionType === PAYMENT_OPTION ? yield select(cartSelectors.getRedeemLoyaltyPoints) : 0;

    return {
        entries,
        coupon,
        totalAmount,
        discount,
        option,
        giftCards,
        loyalty,
        ...(collectGAInfo && {
            deliveryMode,
            totalItems,
        }),
    };
}

export function* sendDeliveryOptionEvent() {
    const analyticsData = yield call(getArgsFromCartData);
    yield spawn(analytics.handleDeliveryOption, analyticsData);
}

export function* sendPaymentOptionEvent() {
    const analyticsData = yield call(getArgsFromCartData, PAYMENT_OPTION);
    yield spawn(analytics.handlePaymentOption, analyticsData);
}

function* incrementCurrentWrapperCounter({ payload }) {
    const analyticsCounter = yield select(selectors.getAnalyticsCounter);

    const currentTypeGroup = analyticsCounter.types.filter(e => e.type === payload.type);

    const counter = currentTypeGroup.length + 1;

    yield put(
        actions.incrementCurrentWrapperCounter({
            ...payload,
            counter,
        }),
    );
}

export function* onCloseDialog({ payload }) {
    if (payload && payload.giftWrapAbort) {
        yield spawn(analytics.handleGiftWrappingAbortEvent);
    }
}

export function* onCloseDialogByName({ payload }) {
    if (payload === PRODUCT_WRAP_MODAL) {
        yield spawn(analytics.handleGiftWrappingAbortEvent);
    } else if (payload === PRODUCT_RTS_MODAL) {
        yield spawn(analytics.handleRTSPopupClosedEvent);
        const productCode = yield select(rtsSelectors.getRtsProductCode);
        if (productCode !== '') {
            const productName = yield select(rtsSelectors.getRtsProductRangeName);
            yield spawn(analytics.handleRTSPopupWithDataClosedEvent, productCode, productName);
        }
    }
}

export function* onOpenDialogByName({ payload }) {
    if (payload.name === PRODUCT_REVIEW_MODAL) {
        yield spawn(analytics.handleReviewProduct, 0);
        const startTime = new Date().getTime();
        yield put(actions.setAddReviewStartTime(startTime));
    }
}

export function* giftSampleStart() {
    yield spawn(analytics.handleGiftSampleStartEvent);
    yield take(uiTypes.CLOSE_DIALOG_BY_NAME);
    const samples = yield select(freeSampleSelectors.getSamples) || [];
    const cartEntries = yield select(cartSelectors.getEntries) || [];

    const selectedSamples = samples.filter(sample =>
        cartEntries.find(cartEntry => cartEntry.product.code === sample.code),
    );

    if (selectedSamples.length) {
        yield spawn(
            analytics.handleGiftSampleConfirmEvent,
            selectedSamples.map(sample => ({ sample_name: sample.name })),
        );
    } else {
        yield spawn(analytics.handleGiftSampleAbortEvent);
    }
}

export function* onCheckoutAuthOptionsInitialDataSet({ payload }) {
    const checkoutOption =
        payload &&
        payload.data &&
        payload.data.checkoutOptions &&
        payload.data.checkoutOptions.value;
    take(getTypes(storeNames.CHECKOUT_AUTH_OPTIONS).AFTER_INITIALIZE);
    const authActions = yield take([
        getTypes(storeNames.CHECKOUT_AUTH_OPTIONS).SET_DATA_FIELD_VALUE,
        authTypes.SET_FOCUSED,
    ]);
    if (authActions.type === authTypes.SET_FOCUSED) {
        yield spawn(analytics.handleSelectLoginOptionEvent, checkoutOption);
    }
}

export function* onCheckoutAuthOptionsInitialized() {
    yield spawn(analytics.handleLoginCheckoutPageLoadedEvent);
}

export function* onCheckoutAuthOptionsDataFieldValidated({ payload }) {
    yield spawn(analytics.handleSelectLoginOptionEvent, payload.value);
}

export function* setMiniPopoverStatus({ payload }) {
    if (payload && payload.opened) {
        yield spawn(analytics.handleMinicartPopoverStatusEvent, payload.opened);
    } else {
        yield spawn(analytics.handleMinicartPopoverStatusEvent, payload.opened);
    }
}

export function* setCartPageCCError() {
    const cartData = yield select(cartSelectors.getData);
    if (isEmpty(cartData)) {
        yield take(cartTypes.SET_CART_DATA);
    }

    const errorsCC = yield select(cartSelectors.getExpressPickupValidationMessage);
    const addRTSAnalytics = yield select(configSelectors.addRTSAnalytics);
    const haveOutOfStock = yield select(cartSelectors.getHaveOutOfStock);
    const { code = '' } = yield select(cartSelectors.getDeliveryMode);

    if (
        (addRTSAnalytics && errorsCC.length) ||
        (haveOutOfStock && code.toLowerCase().includes(constants.ccDeliveryName))
    ) {
        const { name } = yield select(cartSelectors.getDeliveryPointOfService);
        yield spawn(analytics.handleOutOfStockErrorForCC, name);
    }
}

export function* handleCameraLoad({ value }) {
    yield spawn(analytics.handleCameraLoad, value);
}

export function* handleChooseModifaceMode({ payload }) {
    yield spawn(analytics.handleChooseModifaceMode, {
        mode: payload.mode,
        productCode: payload.productCode,
    });
}

export default [
    takeLatest(types.COLLECT_IMPRESSION, sendImpressionViewsData),
    takeLatest(types.IMPRESSION_CLICK, sendImpressionClickData),
    takeLatest(types.SET_REVIEWS_IMPRESSION_VIEW, sendReviewsImpressionViewsData),
    takeLatest(types.SEND_VIEW_EVENT_WAITING, sendViewEventWaiting),
    takeLatest(types.SEND_TRANSACTION_WAITING, sendTransactionWaiting),
    takeEvery(globalTypes.PAGE_LOAD, checkForOutboundLink),
    takeEvery(types.CHECKOUT_CART_EVENTS, sendCheckoutCartEvents),
    takeEvery(types.CHECKOUT_PAYMENT_EVENTS, sendCheckoutPaymentEvents),
    takeEvery(types.SET_CURRENT_STEP, onSetStep),
    takeEvery(checkoutAuthOptionsTypes.SET_DATA_FIELD_VALUE, function*({ payload: { value } }) {
        yield put(actions.setCheckoutOption(value));
    }),
    takeEvery(userTypes.SET_PROFILE, onCheckoutLogin),
    takeEvery(types.SET_CHECKOUT_OPTION, onSetCheckoutOption),
    takeEvery(checkoutAuthOptionsTypes.SET_INITIAL_DATA, function*() {
        const initialOption = yield select(state =>
            checkoutAuthOptionsSelectors.getDataFieldValue(state, 'checkoutOptions'),
        );
        yield put(actions.setCheckoutOption(initialOption));
    }),
    takeEvery(getTypes(storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM).SET_AUTH_OPTION, function*({
        payload: { option },
    }) {
        if (option === CHECKOUT_REGISTRATION) {
            yield put(actions.setCheckoutOption(option));
        }
    }),
    takeEvery(orderTypes.SET_ORDER_DATA, sendOrderEvents),
    takeEvery(types.CALCULATE_CURRENT_WRAPPER_COUNTER, incrementCurrentWrapperCounter),
    takeEvery(types.SET_CC_ERROR, setCartPageCCError),
    takeEvery(types.SET_DELIVERY_OPTION_COMPLETED, sendDeliveryOptionEvent),
    takeEvery(types.SET_PAYMENT_OPTION_COMPLETED, sendPaymentOptionEvent),
    takeEvery(types.GIFT_SAMPLE_START, giftSampleStart),
    takeEvery(types.SET_MINICART_POPOVER_STATUS, setMiniPopoverStatus),
    takeEvery(uiTypes.CLOSE_DIALOG, onCloseDialog),
    takeEvery(uiTypes.CLOSE_DIALOG_BY_NAME, onCloseDialogByName),
    takeEvery(uiTypes.OPEN_DIALOG, onOpenDialogByName),
    takeEvery(
        getTypes(storeNames.CHECKOUT_AUTH_OPTIONS).SET_INITIAL_DATA,
        onCheckoutAuthOptionsInitialDataSet,
    ),
    takeEvery(
        getTypes(storeNames.CHECKOUT_AUTH_OPTIONS).AFTER_INITIALIZE,
        onCheckoutAuthOptionsInitialized,
    ),
    takeEvery(
        getTypes(storeNames.CHECKOUT_AUTH_OPTIONS).DATA_FIELD_VALIDATION_DONE,
        onCheckoutAuthOptionsDataFieldValidated,
    ),
    takeLatest(types.COLLECT_STORY_IMPRESSION, sendStoryImpressionViewsData),
    takeLatest(types.STORY_IMPRESSION_CLICK, sendStoryImpressionClickData),
    takeEvery(types.HANDLE_CAMERA_ERROR, handleCameraLoad),
    takeEvery(types.HANDLE_CHOOSE_MODIFACE_MODE, handleChooseModifaceMode),
];
